import { useState, useEffect, useRef } from 'react';
import { useLang } from '@/pages/home/context/LanguageContext';

const content = {
  PT: {
    badge: 'Parceiro Técnico Estratégico',
    phrases: [
      {
        title1: 'Excelência Técnica para',
        titleHighlight: 'Operações Industriais',
        title2: 'Contínuas',
      },
      {
        title1: 'Soluções Avançadas em',
        titleHighlight: 'Sistemas AVAC',
        title2: 'e Manutenção Industrial',
      },
    ],
    subtitle: 'Especialistas em manutenção de sistemas AVAC, instalações industriais e serviços técnicos para o setor petrolífero. Segurança, eficiência e confiabilidade operacional.',
    btnServices: 'Nossos Serviços',
    btnContact: 'Falar com Especialista',
  },
  EN: {
    badge: 'Strategic Technical Partner',
    phrases: [
      {
        title1: 'Technical Excellence for',
        titleHighlight: 'Continuous Industrial',
        title2: 'Operations',
      },
      {
        title1: 'Advanced Solutions in',
        titleHighlight: 'HVAC Systems',
        title2: 'and Industrial Maintenance',
      },
    ],
    subtitle: 'Specialists in HVAC system maintenance, industrial installations and technical services for the oil sector. Safety, efficiency and operational reliability.',
    btnServices: 'Our Services',
    btnContact: 'Talk to a Specialist',
  },
};

const TYPE_SPEED = 55;
const DELETE_SPEED = 28;
const PAUSE_AFTER_TYPE = 2200;
const PAUSE_AFTER_DELETE = 500;

interface PhraseConfig {
  title1: string;
  titleHighlight: string;
  title2: string;
}

function useLoopTypewriterMulti(phrases: PhraseConfig[]) {
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting' | 'waiting'>('waiting');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentPhrase = phrases[phraseIndex];
  const fullText = `${currentPhrase.title1} ${currentPhrase.titleHighlight} ${currentPhrase.title2}`;

  useEffect(() => {
    setDisplayed('');
    setPhase('waiting');
  }, [phraseIndex]);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (phase === 'waiting') {
      timeoutRef.current = setTimeout(() => setPhase('typing'), 400);
      return;
    }

    if (phase === 'typing') {
      if (displayed.length < fullText.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(fullText.slice(0, displayed.length + 1));
        }, TYPE_SPEED);
      } else {
        timeoutRef.current = setTimeout(() => setPhase('pausing'), PAUSE_AFTER_TYPE);
      }
      return;
    }

    if (phase === 'pausing') {
      timeoutRef.current = setTimeout(() => setPhase('deleting'), 0);
      return;
    }

    if (phase === 'deleting') {
      if (displayed.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(displayed.slice(0, displayed.length - 1));
        }, DELETE_SPEED);
      } else {
        timeoutRef.current = setTimeout(() => {
          setPhraseIndex((prev) => (prev + 1) % phrases.length);
        }, PAUSE_AFTER_DELETE);
      }
      return;
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [phase, displayed, fullText, phrases.length]);

  return { displayed, currentPhrase };
}

export default function HeroSection() {
  const { lang } = useLang();
  const t = content[lang];

  const { displayed, currentPhrase } = useLoopTypewriterMulti(t.phrases);

  const title1End = currentPhrase.title1.length;
  const highlightStart = currentPhrase.title1.length + 1;
  const highlightEnd = currentPhrase.title1.length + 1 + currentPhrase.titleHighlight.length;

  const renderTitle = () => {
    if (displayed.length <= title1End) {
      return <span className="text-white">{displayed}</span>;
    }
    if (displayed.length <= highlightEnd) {
      return (
        <>
          <span className="text-white">{displayed.slice(0, title1End)}</span>
          <span className="text-[#F97316]">{displayed.slice(highlightStart)}</span>
        </>
      );
    }
    return (
      <>
        <span className="text-white">{displayed.slice(0, title1End)}</span>
        <span className="text-[#F97316]">{` ${currentPhrase.titleHighlight}`}</span>
        <span className="text-white">{displayed.slice(highlightEnd)}</span>
      </>
    );
  };

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative w-full h-screen min-h-[600px] flex items-center justify-center overflow-hidden"
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://public.readdy.ai/ai/img_res/edited_db03342f57c7ad5565c133b8fa6d7188_75933cd6.jpg')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B1A35]/90 via-[#0B1A35]/70 to-[#0B1A35]/40" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col items-start justify-center">
        <div
          className="inline-flex items-center gap-2 border border-[#F97316] text-[#F97316] text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-6"
          style={{ animation: 'fadeInDown 0.6s ease both' }}
        >
          <i className="ri-shield-check-line"></i>
          {t.badge}
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-tight max-w-3xl mb-6" style={{ minHeight: '1.15em' }}>
          {renderTitle()}
          <span className="inline-block w-[3px] h-[0.82em] bg-[#F97316] ml-1 align-middle" style={{ animation: 'blink 0.9s step-end infinite' }} />
        </h1>

        <p
          className="text-white/75 text-base md:text-lg max-w-xl mb-10 leading-relaxed"
          style={{ animation: 'fadeInUp 0.8s ease 0.8s both' }}
        >
          {t.subtitle}
        </p>

        <div
          className="flex flex-col sm:flex-row gap-4"
          style={{ animation: 'fadeInUp 0.8s ease 1.1s both' }}
        >
          <button
            onClick={() => handleNav('#services')}
            className="bg-[#F97316] hover:bg-[#ea6c0a] text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-tools-line"></i>
            {t.btnServices}
          </button>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/50 text-xs">Scroll</span>
        <i className="ri-arrow-down-line text-white/50 text-lg"></i>
      </div>

      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(-16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>
    </section>
  );
}
